/**
 * Converts a number to a string with a specified number of decimal places, using a comma as the decimal separator.
 * If decimals is not provided, it keeps the original number of decimals.
 * @param value - The number to convert.
 * @param decimals - The number of decimal places (optional).
 * @returns A string representing the number with the specified number of decimal places.
 */
export const numberToDecimalString = (value: number, decimals?: number): string => {
  const stringValue = decimals !== undefined ? value.toFixed(decimals) : value.toString();
  return stringValue.replace('.', ',');
};

/**
 * Converts a localized decimal string with a comma as the decimal separator to a number.
 * If decimals is not provided, it keeps the original number of decimals.
 * @param value - The localized decimal string to convert.
 * @param decimals - The number of decimal places to round to (optional).
 * @returns The converted number.
 */
export const decimalStringToNumber = (value: string, decimals?: number): number => {
  const parsedValue = parseFloat(value.replace(',', '.'));
  return decimals !== undefined ? parseFloat(parsedValue.toFixed(decimals)) : parsedValue;
};

export const replaceCommasWithDots = (value?: string) => {
  if (value == null) return '';
  return value?.replace(/,/g, '.');
};

export const replaceDotsWithCommas = (value?: string | number) => {
  if (value == null) return '';

  const stringValue = value.toString();

  // Only convert periods to commas for numeric values
  if (/^-?(0|[1-9]\d*)([.,]\d+)?$/.test(stringValue)) {
    return stringValue.replace(/\./g, ',');
  }

  // Return non-numeric strings unchanged
  return stringValue;
};

export const formatAmountCurrency = (
  value: unknown,
  options?: { currency?: boolean; decimals?: number; currencyCode?: string; useGrouping?: boolean }
) => {
  const { currency = false, decimals = 2, currencyCode = 'NOK', useGrouping = true } = options || {};
  let number: number | null | undefined;
  if (typeof value === 'string') {
    number = decimalStringToNumber(value, decimals);
  } else if (typeof value === 'number') {
    number = value;
  } else {
    number = null;
  }
  if (!number && number !== 0) return '';
  return new Intl.NumberFormat('sv-SE', {
    // nye versjonen av chrome 92 støtter ikke nb, no-NB eller nb-NO.
    // benytter det svanske formatet sv-SE som viser beløp i samme format som norge
    // Følgende sak er logget: https://bugs.chromium.org/p/chromium/issues/detail?id=1233047&q=component%3ABlink%3EJavaScript%3EInternationalization
    // Når saken er løst bør sv-SE byttes med no-NB eller nb
    useGrouping,
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...((currency || currencyCode !== 'NOK') && { style: 'currency', currency: currencyCode }),
  }).format(number);
};
